import {AssetSnapshot} from '@hconnect/common'
import {Status} from '@hconnect/common/types/enum.types'
import {
  ChartHeaderDatetimeRows,
  GanttChartRows,
  ChartNowIndicator
} from '@hconnect/ganttchart/src/modules'
import {AddGanttChartItemProps} from '@hconnect/ganttchart/src/types/common'
import {dataTestId} from '@hconnect/uikit'
import {Box, Skeleton} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import React from 'react'

import {usePlanningChartScheduleData} from '../../../app/page-planning/dailyPlanning/PlanningChartScheduleDataProvider'
import {usePlanningChartStartEnd} from '../../../app/page-planning/dailyPlanning/PlanningChartStartEndProvider'
import {ScheduleItemsContainer} from '../../../app/page-planning/dailyPlanning/scheduleCard/schedule'
import {TimeScaleFn, useLinearScale} from '../../helpers/scale'
import {toPixel} from '../../helpers/utils'
import {
  useElectricityPricesDataQuery,
  useHistoryAssetsByIdQuery,
  useHistoryAssetsQuery
} from '../../hooks/api'
import {useAddScheduleItem} from '../../hooks/api/schedules/useAddScheduleItem'
import {useScheduleQuery} from '../../hooks/api/schedules/useScheduleQuery'
import {useSubmitSchedule} from '../../hooks/api/schedules/useSubmitSchedule'
import {usePlantConfig} from '../../hooks/usePlantConfigData'
import {ScheduleOtimizationRange} from '../../interfaces/api'

import {ElectricityPriceTopBar} from './electricityPriceTopBar/ElectricityPriceTopBar'
import {ChartOptimizedPlanStartEndIndicator} from './indicators/ChartOptimizedPlanStartEndIndicator'

const VISIBLE_CELL_TEXT_BREAKPOINT = 24
const SCHEDULE_ROW_PADDING = 7

interface PlanningChartGanttProps {
  startOfPlan: Moment
  endOfPlan: Moment
  optimizationRange?: ScheduleOtimizationRange
  dateLabelHeight: number
  hourlyPriceHeight: number
  cellHeight: number
  cellWidth: number
  hoursDisplayed: number
  numberOfAssets: number | undefined
  xScale: TimeScaleFn
  hoursList: Moment[]
  isReadOnly: boolean
  shouldShowCurrentTime?: boolean
  shouldRoundCurrentTime?: boolean
  shouldShowExactPrices?: boolean
  shouldShowActualPricesIndicator?: boolean
  shouldShowColorCodedElectricityPrices?: boolean
  shouldShowFixedForOptimizerInfo?: boolean
  shouldShowStartEndTime?: boolean
  shouldShowScheduleItemComments?: boolean
}
export const _PlanningChartGantt = React.forwardRef<HTMLDivElement, PlanningChartGanttProps>(
  (
    {
      startOfPlan,
      endOfPlan,
      dateLabelHeight,
      hourlyPriceHeight,
      cellHeight,
      cellWidth,
      hoursDisplayed,
      xScale,
      hoursList,
      numberOfAssets,
      isReadOnly,
      optimizationRange,
      shouldShowCurrentTime = false,
      shouldRoundCurrentTime = false,
      shouldShowExactPrices = false,
      shouldShowActualPricesIndicator = false,
      shouldShowColorCodedElectricityPrices = false,
      shouldShowFixedForOptimizerInfo = false,
      shouldShowStartEndTime = false,
      shouldShowScheduleItemComments = false
    },
    ref
  ) => {
    const {data: assetsById} = useHistoryAssetsByIdQuery({timeFrame: [startOfPlan, endOfPlan]})
    const scheduleRowHeight = cellHeight - 2 * SCHEDULE_ROW_PADDING
    const {
      startOfChart,
      endOfChart,
      visibleHoursList,
      hoursRange: [displayedHoursOffset]
    } = usePlanningChartStartEnd()
    const {timezone_id: timezoneId} = usePlantConfig()

    const {data: assets} = useHistoryAssetsQuery({
      timeFrame: [startOfPlan, endOfPlan],
      sorted: true
    })

    const {data: electricityPriceData} = useElectricityPricesDataQuery([startOfPlan, endOfPlan])
    const {isPending: isScheduleSubmitting} = useSubmitSchedule()
    const {scheduleId} = usePlanningChartScheduleData()
    const {isFetching: isScheduleFetching} = useScheduleQuery({
      range: [startOfPlan, endOfPlan],
      scheduleId
    })
    const shouldDisableGanttRows = isScheduleSubmitting || isScheduleFetching || isReadOnly
    const {priceLevels, pricePerHour, mergedPrices: prices} = electricityPriceData ?? {}

    const addScheduleItem = useAddScheduleItem({
      startOfPlan,
      endOfPlan
    })

    const onAddScheduleItem = ({
      rowId: assetId,
      selectedItemXAxisValue: start
    }: AddGanttChartItemProps<Moment>) => {
      const firstAssetOperationModeId: number | undefined =
        assetsById && assetId !== undefined
          ? assetsById[assetId]?.operationModes.filter(
              (operationMode) => operationMode.status !== Status.Deleted
            )?.[0]?.id
          : undefined
      if (firstAssetOperationModeId !== undefined) {
        addScheduleItem({
          start: start.toISOString(),
          end: moment.min(start.clone().add(3, 'hours'), endOfPlan).toISOString(),
          assetOperationModeId: firstAssetOperationModeId,
          assetId,
          isTransitionTime: false
        })
      }
    }

    const ganttYScale = useLinearScale({
      domain: [0, numberOfAssets ?? 0],
      range: [0, cellHeight * (numberOfAssets ?? 0)]
    })

    const chartWidth = hoursDisplayed * cellWidth
    const isTextVisible = cellWidth > VISIBLE_CELL_TEXT_BREAKPOINT

    const shouldShowLoader = numberOfAssets === undefined

    const shouldShowPricesRow = shouldShowExactPrices || shouldShowColorCodedElectricityPrices

    return (
      <Box position="relative" width={chartWidth}>
        <ChartHeaderDatetimeRows
          cellWidth={cellWidth}
          isTextVisible={isTextVisible}
          dateLabelHeight={dateLabelHeight}
          visibleHoursList={visibleHoursList}
        />
        <ChartNowIndicator
          hoursDisplayed={hoursDisplayed}
          cellWidth={cellWidth}
          shouldRoundCurrentTime={shouldRoundCurrentTime}
          startOfChart={startOfChart}
          endOfChart={endOfChart}
          timezoneId={timezoneId}
        />
        {optimizationRange && (
          <ChartOptimizedPlanStartEndIndicator
            hoursDisplayed={hoursDisplayed}
            cellWidth={cellWidth}
            optimizationRange={optimizationRange}
          />
        )}
        <Box sx={{mt: toPixel(dateLabelHeight * 2)}}>
          {shouldShowPricesRow && (
            <ElectricityPriceTopBar
              hoursList={hoursList}
              shouldShowExactPrices={shouldShowExactPrices}
              shouldShowColorCodedPrices={shouldShowColorCodedElectricityPrices}
              shouldShowActualPricesIndicator={shouldShowActualPricesIndicator}
              hoursDisplayed={hoursDisplayed}
              isTextVisible={isTextVisible}
              hourlyPriceHeight={hourlyPriceHeight}
              pricePerHour={pricePerHour}
              cellWidth={cellWidth}
            />
          )}
        </Box>
        <Box sx={{width: chartWidth, height: 1, overflow: 'hidden', position: 'relative'}}>
          {shouldShowLoader ? (
            <Skeleton
              variant="rectangular"
              sx={{
                height: 1,
                width: hoursList.length * cellWidth
              }}
              {...dataTestId('planning_chart_gantt_skeleton')}
            />
          ) : (
            <GanttChartRows<AssetSnapshot, Moment>
              chartStartBoundary={startOfPlan}
              chartEndBoundary={endOfPlan}
              stepWidth={cellWidth}
              displayCurrentTime={{
                xScale,
                timezoneId,
                shouldRoundCurrentTime,
                shouldShowCurrentTime
              }}
              isReadOnly={shouldDisableGanttRows}
              xAxis={visibleHoursList}
              canvasOffset={displayedHoursOffset}
              onAddItem={onAddScheduleItem}
              ganttChartRows={assets ?? []}
            />
          )}
          <div
            // passing ref here to enable tranform translateX to move chart horizontally
            ref={ref}
            style={{
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'none'
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: chartWidth,
                height: cellHeight * (numberOfAssets ?? 10)
              }}
            >
              {prices && !shouldShowLoader && (
                <ScheduleItemsContainer
                  startOfPlan={startOfPlan}
                  endOfPlan={endOfPlan}
                  priceLevels={priceLevels}
                  prices={prices}
                  scheduleRowHeight={scheduleRowHeight}
                  scheduleRowPadding={SCHEDULE_ROW_PADDING}
                  xScale={xScale}
                  yScale={ganttYScale}
                  isReadOnly={isReadOnly}
                  shouldShowFixedForOptimizerInfo={shouldShowFixedForOptimizerInfo}
                  shouldShowColorCodedElectricityPrices={shouldShowColorCodedElectricityPrices}
                  shouldShowExactElectricityPrices={shouldShowExactPrices}
                  shouldShowStartEndTime={shouldShowStartEndTime}
                  shouldShowScheduleItemComments={shouldShowScheduleItemComments}
                />
              )}
            </Box>
          </div>
        </Box>
      </Box>
    )
  }
)
export const PlanningChartGantt = React.memo(_PlanningChartGantt)
PlanningChartGantt.displayName = 'PlanningChartGantt'
